/**
 * Custom CKEditor 5 upload adapter for predefined builds (classic, decoupled).
 * Predefined builds do not include SimpleUploadAdapter, so we set
 * FileRepository.createUploadAdapter in onReady with this adapter.
 *
 * @see https://ckeditor.com/docs/ckeditor5/latest/framework/deep-dive/upload-adapter.html
 * @see https://ckeditor.com/docs/ckeditor5/latest/support/error-codes.html#error-filerepository-no-upload-adapter
 */

const UPLOAD_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/learning/upload-image`
  : '/api/learning/upload-image';

function getAuthHeader() {
  try {
    const token = localStorage.getItem('token');
    if (token && token !== 'undefined' && token !== 'null' && token.trim() !== '') {
      return { Authorization: `Bearer ${token}` };
    }
  } catch (_) {}
  return {};
}

/**
 * Custom upload adapter that POSTs the file to our backend and returns the image URL.
 * Implements the UploadAdapter interface: upload() and abort().
 */
class CKEditorUploadAdapter {
  constructor(loader) {
    this.loader = loader;
    this.xhr = null;
  }

  upload() {
    return this.loader.file.then(
      (file) =>
        new Promise((resolve, reject) => {
          this.xhr = new XMLHttpRequest();
          this.xhr.open('POST', UPLOAD_URL, true);
          this.xhr.responseType = 'json';

          const headers = getAuthHeader();
          Object.keys(headers).forEach((key) => {
            this.xhr.setRequestHeader(key, headers[key]);
          });

          this.xhr.addEventListener('error', () => {
            reject(new Error(`Could not upload file: ${file.name}.`));
          });
          this.xhr.addEventListener('abort', () => reject(null));
          this.xhr.addEventListener('load', () => {
            const response = this.xhr.response;
            if (!response || response.error) {
              return reject(
                response?.error?.message || new Error(`Could not upload file: ${file.name}.`)
              );
            }
            // CKEditor expects { default: url } or { default, '160', '500', ... } for srcset
            resolve(
              response.url
                ? { default: response.url }
                : response.urls || { default: '' }
            );
          });

          if (this.xhr.upload) {
            this.xhr.upload.addEventListener('progress', (evt) => {
              if (evt.lengthComputable) {
                this.loader.uploadTotal = evt.total;
                this.loader.uploaded = evt.loaded;
              }
            });
          }

          const data = new FormData();
          data.append('upload', file);
          this.xhr.send(data);
        })
    );
  }

  abort() {
    if (this.xhr) {
      this.xhr.abort();
    }
  }
}

/**
 * Call this from CKEditor's onReady(editor) to enable image upload.
 * Sets FileRepository.createUploadAdapter so the "insert image" and paste/drop
 * flows use our backend.
 *
 * @param {object} editor - CKEditor 5 editor instance
 */
export function configureCKEditorUpload(editor) {
  const fileRepository = editor.plugins.get('FileRepository');
  if (fileRepository) {
    fileRepository.createUploadAdapter = (loader) => new CKEditorUploadAdapter(loader);
  }
}

export default CKEditorUploadAdapter;
